import React, { useRef, useState, useEffect } from "react";
import styles from "./Time.module.css";
import { useTranslation } from "react-i18next";

const DatePicker2 = ({ setDate, startDate, endDate }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const start = startDate ? new Date(startDate) : new Date();
  const end = endDate ? new Date(endDate) : new Date(start.getFullYear(), start.getMonth() + 1, 0);

  const [selectedYear, setSelectedYear] = useState(start.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(start.getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(start.getDate());

  const yearRef = useRef(null);
  const monthRef = useRef(null);
  const dayRef = useRef(null);

  // Generate years within range
  const years = Array.from({ length: end.getFullYear() - start.getFullYear() + 1 }, (_, i) => start.getFullYear() + i);

  // Generate months within range
  const allMonths = [
    { number: 1, name: t("monthNames.january") },
    { number: 2, name: t("monthNames.february") },
    { number: 3, name: t("monthNames.march") },
    { number: 4, name: t("monthNames.april") },
    { number: 5, name: t("monthNames.may") },
    { number: 6, name: t("monthNames.june") },
    { number: 7, name: t("monthNames.july") },
    { number: 8, name: t("monthNames.august") },
    { number: 9, name: t("monthNames.september") },
    { number: 10, name: t("monthNames.october") },
    { number: 11, name: t("monthNames.november") },
    { number: 12, name: t("monthNames.december") },
  ].filter((month) => {
    if (selectedYear === start.getFullYear() && month.number < start.getMonth() + 1) return false;
    if (selectedYear === end.getFullYear() && month.number > end.getMonth() + 1) return false;
    return true;
  });

  // Generate days within range
  const generateDays = (year, month) => {
    let firstDay = 1;
    let lastDay = new Date(year, month, 0).getDate();

    if (year === start.getFullYear() && month === start.getMonth() + 1) {
      firstDay = start.getDate();
    }
    if (year === end.getFullYear() && month === end.getMonth() + 1) {
      lastDay = end.getDate();
    }

    return Array.from({ length: lastDay - firstDay + 1 }, (_, i) => firstDay + i);
  };

  const handleYearChange = (year) => {
    if (year < start.getFullYear() || year > end.getFullYear()) return;
  
    setSelectedYear(year);
  
    // Adjust months if the selected year is at the range boundaries
    if (year === start.getFullYear()) {
      if (selectedMonth < start.getMonth() + 1) {
        setSelectedMonth(start.getMonth() + 1);
        setSelectedDay(start.getDate());
      }
    } else if (year === end.getFullYear()) {
      if (selectedMonth > end.getMonth() + 1) {
        setSelectedMonth(end.getMonth() + 1);
        setSelectedDay(end.getDate());
      }
    }
  };

  const handleMonthChange = (month) => {
    if (
      (selectedYear === start.getFullYear() && month < start.getMonth() + 1) ||
      (selectedYear === end.getFullYear() && month > end.getMonth() + 1)
    ) return;
  
    setSelectedMonth(month);
  
    // Adjust days if the selected month is at the range boundaries
    if (selectedYear === start.getFullYear() && month === start.getMonth() + 1) {
      if (selectedDay < start.getDate()) {
        setSelectedDay(start.getDate());
      }
    } else if (selectedYear === end.getFullYear() && month === end.getMonth() + 1) {
      if (selectedDay > end.getDate()) {
        setSelectedDay(end.getDate());
      }
    }
  };
  

  const [days, setDays] = useState(generateDays(selectedYear, selectedMonth));

  useEffect(() => {
    setDays(generateDays(selectedYear, selectedMonth));
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    const formattedDate = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`;
    setDate(formattedDate);
  }, [selectedYear, selectedMonth, selectedDay, setDate]);

  const handleScroll = (ref, direction) => {
    if (!ref.current) return;
    
    let scrollInterval = setInterval(() => {
      ref.current.scrollLeft += direction * 10;
    }, 10);
  
    const stopScroll = () => {
      clearInterval(scrollInterval);
      window.removeEventListener("mouseup", stopScroll);
      window.removeEventListener("touchend", stopScroll);
    };
  
    window.addEventListener("mouseup", stopScroll);
    window.addEventListener("touchend", stopScroll);
  };

  
  return (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
      {/* Year Selector */}
      <div className={styles.years}>
        <div className={styles.label} style={{ borderRadius: currentLanguage === "ar" ? "0 8px 8px 0" : "8px 0 0 8px" }}>
          {t("year")}
        </div>

        <div style={{
          display:"flex",
          justifyContent:"space-between",
          alignItems:"center",
          width:"85%"
        }}>
          <button 
            className={styles.arrowButton} 
            onMouseDown={() => handleScroll(yearRef, 10)}
            onTouchStart={() => handleScroll(yearRef, 10)}
          >
            {"<"}
          </button>
          <div 
            className={styles.scrollContainer} 
            ref={yearRef}
            style={{ 
              WebkitOverflowScrolling: 'touch',
              overflowX: 'auto'
            }}
          >
            {years.map((year) => (
              <button
                key={year}
                className={`${styles.button} ${year === selectedYear ? styles.selected : ""}`}
                onClick={() => handleYearChange(year)}
              >
                {year}
              </button>
            ))}
          </div>
          <button 
            className={styles.arrowButton} 
            onMouseDown={() => handleScroll(yearRef, -10)}
            onTouchStart={() => handleScroll(yearRef, -10)}
          >
            {">"}
          </button>
        </div>
      </div>

      {/* Month Selector */}
      <div className={styles.years} style={{ marginTop: "10px" }}>
        <div className={styles.label} style={{ borderRadius: currentLanguage === "ar" ? "0 8px 8px 0" : "8px 0 0 8px" }}>
          {t("month")}
        </div>
        <div style={{
          display:"flex",
          justifyContent:"space-between",
          alignItems:"center",
          width:"85%"
        }}>
          <button 
            className={styles.arrowButton} 
            onMouseDown={() => handleScroll(monthRef, 10)}
            onTouchStart={() => handleScroll(monthRef, 10)}
          >
            {"<"}
          </button>
          <div 
            className={styles.scrollContainer} 
            ref={monthRef}
            style={{ 
              WebkitOverflowScrolling: 'touch',
              overflowX: 'auto'
            }}
          >
            {allMonths.map((month) => (
              <button
                key={month.number}
                className={`${styles.button} ${month.number === selectedMonth ? styles.selected : ""}`}
                onClick={() => handleMonthChange(month.number)}
              >
                {month.name}
              </button>
            ))}
          </div>
          <button 
            className={styles.arrowButton} 
            onMouseDown={() => handleScroll(monthRef, -10)}
            onTouchStart={() => handleScroll(monthRef, -10)}
          >
            {">"}
          </button>
        </div>
      </div>

      {/* Day Selector */}
      <div className={styles.years} style={{ marginTop: "10px" }}>
        <div className={styles.label} style={{ borderRadius: currentLanguage === "ar" ? "0 8px 8px 0" : "8px 0 0 8px" }}>
          {t("day")}
        </div>
        <div style={{
          display:"flex",
          justifyContent:"space-between",
          alignItems:"center",
          width:"85%"
        }}>
          <button 
            className={styles.arrowButton} 
            onMouseDown={() => handleScroll(dayRef, 10)}
            onTouchStart={() => handleScroll(dayRef, 10)}
          >
            {"<"}
          </button>
          <div 
            className={styles.scrollContainerDays} 
            ref={dayRef}
            style={{ 
              WebkitOverflowScrolling: 'touch',
              overflowX: 'auto'
            }}
          >
            {days.map((day) => (
              <button
                key={day}
                className={`${styles.buttonDays} ${day === selectedDay ? styles.selected : ""}`}
                onClick={() => setSelectedDay(day)}
              >
                {day}
              </button>
            ))}
          </div>
          <button 
            className={styles.arrowButton} 
            onMouseDown={() => handleScroll(dayRef, -10)}
            onTouchStart={() => handleScroll(dayRef, -10)}
          >
            {">"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatePicker2;